import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Grid,
  Stack,
} from "@mui/material";
import {
  Video,
  Sparkles,
  Layers,
  Gamepad2,
  Gift,
  DollarSign,
  Compass,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { networkID } from "../../../../../static/utils";
import { getNetworkName, LOTTERY_NAMES } from "../../../../../static/Constants";
import HelpVideoCard from "./HelpVideoCard";
import HelpEmptyState from "./HelpEmptyState";

export const getHelpVideos = () => {
  const currentNetworkName = getNetworkName(networkID);

  return [
    {
      id: 1,
      title: "How to register as creator",
      youtubeId: "n42U8f94Pek",
      category: "creator",
      badge: "Creator Onboarding",
      description: `Complete step-by-step walkthrough to become a verified lottery creator on ${currentNetworkName}.`,
    },
    {
      id: 2,
      title: "How to register as a partner",
      youtubeId: "aY84Xf2-08k",
      category: "creator",
      badge: "Partner Onboarding",
      description: "Learn how to register custom brand profiles, deploy partner tokens, and link lottery contracts.",
    },
    {
      id: 3,
      title: `How to create a Revolver (${LOTTERY_NAMES.SPINBET}) lottery`,
      youtubeId: "kAAK7fPCeb4",
      category: "creating",
      badge: `${LOTTERY_NAMES.SPINBET} Creation`,
      description: "Configure entry fees, prize pools, and spin wheel parameters for instant revolver games.",
    },
    {
      id: 4,
      title: "How to create a Mine lottery",
      youtubeId: "eHe5smofJFw",
      category: "creating",
      badge: `${LOTTERY_NAMES.MINEBET} Creation`,
      description: `Set up capacity and entry prices for direct single-pick ${LOTTERY_NAMES.MINEBET} rounds.`,
    },
    {
      id: 5,
      title: `How to create an MRL (${LOTTERY_NAMES.MEGABET}) lottery`,
      youtubeId: "dKw-DDT88oM",
      category: "creating",
      badge: `${LOTTERY_NAMES.MEGABET} Creation`,
      description: `Configure multi-number matrixes, accumulator rollovers, and capacity for ${LOTTERY_NAMES.MEGABET} raffles.`,
    },
    {
      id: 6,
      title: `How to create a Missile (${LOTTERY_NAMES.ROCKETBET}) lottery`,
      youtubeId: "gR9xiGbp7gc",
      category: "creating",
      badge: `${LOTTERY_NAMES.ROCKETBET} Creation`,
      description: `Configure launch stages, target numbers, and multipliers for ${LOTTERY_NAMES.ROCKETBET} lotteries.`,
    },
    {
      id: 7,
      title: "How to buy a Revolver lottery ticket",
      youtubeId: "QJ1RCxfFqh0",
      category: "playing",
      badge: `${LOTTERY_NAMES.SPINBET} Ticket`,
      description: "Select your number on the wheel and confirm the instant on-chain spin transaction.",
    },
    {
      id: 8,
      title: "How to buy a Mine lottery ticket",
      youtubeId: "gqZ3Uo96lP4",
      category: "playing",
      badge: `${LOTTERY_NAMES.MINEBET} Ticket`,
      description: "Pick your lucky mine slot and confirm your entry in the active round.",
    },
    {
      id: 9,
      title: "How to buy an MRL lottery ticket",
      youtubeId: "f3c3W-bE7y8",
      category: "playing",
      badge: `${LOTTERY_NAMES.MEGABET} Ticket`,
      description: `Choose single or multiple lucky ticket combinations for the next ${LOTTERY_NAMES.MEGABET} draw.`,
    },
    {
      id: 10,
      title: "How to buy a Missile lottery ticket",
      youtubeId: "h-P8_Lz1P_8",
      category: "playing",
      badge: `${LOTTERY_NAMES.ROCKETBET} Ticket`,
      description: "Select your launch numbers and participate in the high-multiplier progressive round.",
    },
    {
      id: 11,
      title: "How to create a gift card",
      youtubeId: "aR18o59qXo4",
      category: "giftcards",
      badge: "Gift Cards",
      description: "Issue customized digital token gift cards and promotional vouchers on AutoBET.",
    },
    {
      id: 12,
      title: "How to buy a gift card",
      youtubeId: "4TqH5i2K7qE",
      category: "giftcards",
      badge: "Gift Cards",
      description: "Purchase gift vouchers or claim promotional tokens for lottery gameplay.",
    },
    {
      id: 13,
      title: "How creators can withdraw their earnings",
      youtubeId: "6p6k7j3I2qg",
      category: "payouts",
      badge: "Earnings & Payouts",
      description: "Direct guide for creators to withdraw their earned lottery proceeds and platform profits.",
    },
  ];
};

export const HELP_VIDEOS = getHelpVideos();

const VIDEO_TABS = [
  { id: "all", label: "All Tutorials", icon: Layers, color: colors.primary.main },
  { id: "onboarding", label: "Getting Started", icon: Compass, color: colors.blue[600] },
  { id: "creating", label: "Creator Studio", icon: Sparkles, color: colors.purple[600] },
  { id: "playing", label: "Playing & Buying", icon: Gamepad2, color: colors.emerald[600] },
  { id: "giftcards", label: "Gift Cards", icon: Gift, color: colors.amber[600] },
  { id: "payouts", label: "Earnings & Payouts", icon: DollarSign, color: colors.cyan[600] },
];

const HelpVideoGuides = ({ searchQuery = "", onClearSearch }) => {
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredVideos = useMemo(() => {
    let result = HELP_VIDEOS;

    if (activeCategory !== "all") {
      result = result.filter((v) => v.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.description.toLowerCase().includes(q) ||
          (v.badge && v.badge.toLowerCase().includes(q))
      );
    }

    return result;
  }, [activeCategory, searchQuery]);

  const countsByCategory = useMemo(() => {
    const counts = { all: HELP_VIDEOS.length };
    HELP_VIDEOS.forEach((v) => {
      counts[v.category] = (counts[v.category] || 0) + 1;
    });
    return counts;
  }, []);

  return (
    <Box id="video-tutorials-section" sx={{ mb: 6 }}>
      {/* Card Header with Section Title & Category Tabs */}
      <Card
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: borderRadiusPx.xl,
          backgroundColor: colors.dashboard.cardBg,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          mb: 3.5,
        }}
      >
        <Stack spacing={2.5}>
          {/* Top Title & Badge */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.blue[50],
                  color: colors.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(37, 99, 235, 0.12)",
                }}
              >
                <Video size={22} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "1.25rem" }}>
                  Video Walkthroughs & Guides
                </Typography>
                <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: "0.875rem" }}>
                  Watch concise visual tutorials on using every AutoBET feature
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                px: 1.75,
                py: 0.6,
                borderRadius: borderRadiusPx.full,
                bgcolor: colors.blue[50],
                border: `1px solid ${colors.blue[100]}`,
              }}
            >
              <Typography variant="caption" sx={{ color: colors.primary.main, fontWeight: 700, fontSize: "0.75rem" }}>
                Showing {filteredVideos.length} of {HELP_VIDEOS.length} Videos
              </Typography>
            </Box>
          </Stack>

          {/* Category Tabs */}
          <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            sx={{
              gap: 1.25,
              pt: 0.5,
            }}
          >
            {VIDEO_TABS.map((tab) => {
              const isSelected = activeCategory === tab.id;
              const TabIcon = tab.icon;
              const count = countsByCategory[tab.id] || 0;

              return (
                <Box
                  key={tab.id}
                  onClick={() => setActiveCategory(tab.id)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: borderRadiusPx.lg,
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s ease-in-out",
                    backgroundColor: isSelected ? colors.primary.main : colors.slate[50],
                    border: `1px solid ${isSelected ? colors.primary.main : colors.slate[200]}`,
                    boxShadow: isSelected ? "0 4px 12px rgba(37, 99, 235, 0.25)" : "none",
                    "&:hover": {
                      backgroundColor: isSelected ? colors.primary.dark : colors.slate[100],
                      borderColor: isSelected ? colors.primary.dark : colors.slate[300],
                    },
                  }}
                >
                  <TabIcon size={16} color={isSelected ? colors.neutral.white : tab.color} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isSelected ? 700 : 600,
                      fontSize: "0.875rem",
                      color: isSelected ? colors.neutral.white : colors.slate[700],
                    }}
                  >
                    {tab.label}
                  </Typography>
                  <Box
                    sx={{
                      px: 0.85,
                      py: 0.15,
                      borderRadius: borderRadiusPx.full,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      backgroundColor: isSelected ? "rgba(255, 255, 255, 0.25)" : colors.slate[200],
                      color: isSelected ? colors.neutral.white : colors.slate[700],
                    }}
                  >
                    {count}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      </Card>

      {/* Video Cards Grid */}
      {filteredVideos.length > 0 ? (
        <Grid container spacing={3}>
          {filteredVideos.map((video) => (
            <Grid item xs={12} sm={6} lg={4} key={video.id}>
              <HelpVideoCard video={video} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <HelpEmptyState
          title="No Video Tutorials Found"
          description={`We couldn't find any video tutorials matching "${searchQuery}". Try clearing your search or picking a different category.`}
          actionText="Clear Search & Show All Videos"
          onAction={onClearSearch}
        />
      )}
    </Box>
  );
};

HelpVideoGuides.propTypes = {
  searchQuery: PropTypes.string,
  onClearSearch: PropTypes.func.isRequired,
};

export default HelpVideoGuides;
